/** Single source of truth for how a raw `user.role` maps to a display label and
 *  to the role-filter groups, plus the header-bridge event names for the
 *  workspace Users tab.
 *
 *  Badge rendering and role filtering used to hand-roll two independent
 *  mappings and they drifted: the filter offered admin/staff/client/supervisor
 *  while workspace users actually carry workspace_owner/workspace_admin/
 *  workspace_user, so no filter option ever matched a workspace user.
 *
 *  Event names live here rather than in a separate module because both sides of
 *  the bridge (UnifiedAppHeader and UserManagement) already import this file for
 *  the role options, so co-locating them costs no extra import and removes the
 *  untyped string literals that are this codebase's most common bridge bug. */

export const WORKSPACE_USERS_SEARCH_EVENT = 'workspace-users:search';
export const WORKSPACE_USERS_FILTER_EVENT = 'workspace-users:filter';
export const WORKSPACE_USERS_CREATE_EVENT = 'workspace-users:create';
export const WORKSPACE_USERS_INVITE_CANCEL_EVENT = 'workspace-users:invite-cancel';
export const WORKSPACE_USERS_INVITE_SUBMIT_EVENT = 'workspace-users:invite-submit';
/** Feature -> header sync. `{ listVisible }` while the user list is on screen,
 *  `null` once it isn't, so the header's users pills don't sit over a form. */
export const WORKSPACE_USERS_STATE_EVENT = 'workspace-users:state-change';

/** Edit-user form -> header sync: `{ hasChanges }`, `null` on unmount. Mirrors
 *  the workspace form's `workspace:state-change` bridge. */
export const WORKSPACE_USER_FORM_STATE_EVENT = 'workspace-user-form:state-change';
/** Header -> edit-user form intents. Back and cancel are the same action (both
 *  discard and return to the list); they exist separately because one is the
 *  leading navigation chevron and the other the trailing text pill. */
export const WORKSPACE_USER_FORM_SAVE_EVENT = 'workspace-user-form:save';
export const WORKSPACE_USER_FORM_CANCEL_EVENT = 'workspace-user-form:cancel';
/** Sidebar -> form. The form's sections render as sidebar rows while it is
 *  open (see USER_FORM_SECTION_ITEMS), so the in-content TabsList is dropped
 *  and this is how a row selects a section. */
export const WORKSPACE_USER_FORM_SET_TAB_EVENT = 'workspace-user-form:set-tab';

export type UserRoleGroupId =
  | 'super_admin'
  | 'internal'
  | 'workspace_owner'
  | 'workspace_admin'
  | 'workspace_user';

export interface UserRoleGroup {
  id: UserRoleGroupId;
  label: string;
  /** One-sentence, sentence-case summary of what this role can do. Single
   *  source for every role picker/summary surface (InviteUserForm,
   *  EditUserForm's permissions card) — these used to hand-roll their own
   *  near-identical copy independently and drifted into three slightly
   *  different wordings for the same role. */
  description: string;
  /** Authority ranking. Only meaningful WITHIN a tier today (workspace_owner
   *  3 > workspace_admin 2 > workspace_user 1; the CG tiers rank above every
   *  workspace tier) — not yet consumed anywhere. Reserved for the upcoming
   *  role-model migration's authority comparisons, so that work doesn't have
   *  to re-derive an ordering the display layer already knows. */
  rank: number;
  /** Whether this role can be offered on the workspace invite sheet.
   *  workspace_owner is additionally gated by the internal + no-active-owner
   *  check (InviteUserForm's `canOfferOwner` prop, workspace_invitations_
   *  routes.tsx's matching server-side check) even when true here — this
   *  flag only says the role exists in that picker's vocabulary at all. */
  offerableInWorkspaceInvites: boolean;
  /** Every raw `user.role` value that resolves to this group. Kept exactly
   *  as-is (legacy 'member'/'client' -> workspace_owner) — the pre-invite-
   *  flow vocabulary this maps is retired by the upcoming role-model
   *  migration, not by this registry shape change. */
  roles: string[];
}

export const USER_ROLE_GROUPS: UserRoleGroup[] = [
  {
    id: 'super_admin',
    label: 'Super Admin',
    description: 'Full system access including user management and all commercial features.',
    rank: 5,
    offerableInWorkspaceInvites: false,
    roles: ['super_admin', 'admin'],
  },
  {
    id: 'internal',
    label: 'Internal',
    description: 'Full access to all workspaces. Can manage variations and applications.',
    rank: 4,
    offerableInWorkspaceInvites: false,
    roles: ['internal', 'staff'],
  },
  {
    id: 'workspace_owner',
    label: 'Workspace Owner',
    description: 'Top-level access, including billing and member management.',
    rank: 3,
    offerableInWorkspaceInvites: true,
    roles: ['workspace_owner', 'member', 'client'],
  },
  {
    id: 'workspace_admin',
    label: 'Workspace Admin',
    description: 'Senior workspace access. Owner-only powers such as billing stay with the owner.',
    rank: 2,
    offerableInWorkspaceInvites: true,
    roles: ['workspace_admin'],
  },
  {
    id: 'workspace_user',
    label: 'Workspace User',
    description: 'Site-level access to assigned projects only.',
    rank: 1,
    offerableInWorkspaceInvites: true,
    roles: ['workspace_user', 'supervisor'],
  },
];

/** Groups offerable on the workspace invite sheet, in display order.
 *  workspace_owner is included whenever its own flag is true — callers that
 *  need the internal+ownerless gate applied on top (InviteUserForm) filter
 *  it further themselves; this list is the vocabulary, not the final gate. */
export const WORKSPACE_INVITE_OFFERABLE_GROUPS: UserRoleGroup[] =
  USER_ROLE_GROUPS.filter(group => group.offerableInWorkspaceInvites);

export const USER_ROLE_FILTER_ALL = 'all';

export const USER_ROLE_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: USER_ROLE_FILTER_ALL, label: 'All Roles' },
  ...USER_ROLE_GROUPS.map(({ id, label }) => ({ value: id, label })),
];

const ROLE_TO_GROUP: Map<string, UserRoleGroup> = new Map(
  USER_ROLE_GROUPS.flatMap(group => group.roles.map(role => [role, group] as const)),
);

export function getUserRoleGroupId(role: string | null | undefined): UserRoleGroupId | null {
  return role ? ROLE_TO_GROUP.get(role)?.id ?? null : null;
}

export function getUserRoleLabel(role: string | null | undefined): string {
  return (role ? ROLE_TO_GROUP.get(role)?.label : undefined) ?? 'User';
}

/** One-sentence description for a raw `user.role` value — see
 *  UserRoleGroup.description. Empty string for an unrecognised role, so
 *  callers can render it directly without an extra fallback. */
export function getUserRoleDescription(role: string | null | undefined): string {
  return (role ? ROLE_TO_GROUP.get(role)?.description : undefined) ?? '';
}

/** True when the role belongs to the group, or when no filter is applied. */
export function matchesUserRoleFilter(role: string | null | undefined, groupId: string): boolean {
  if (!groupId || groupId === USER_ROLE_FILTER_ALL) return true;
  return getUserRoleGroupId(role) === groupId;
}

/** Roles that carry administrative authority, for the Admins stat card. */
export function isAdminUserRole(role: string | null | undefined): boolean {
  const id = getUserRoleGroupId(role);
  return id === 'super_admin' || id === 'workspace_admin';
}
